import { Stack, Typography, Button } from "@mui/material";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { InvoiceWithPaymentInformation } from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import WhiteCard from "src/shared/components/WhiteCard";
import { getMedicationForDescription } from "src/utils/getMedicationForDescription";

type Props = {
  invoice: InvoiceWithPaymentInformation;
  page: string;
  handleOpenModal: (i: string) => void;
};

const FormattedInvoice = ({ invoice, page, handleOpenModal }: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const cardInfo = useMemo(() => {
    if (invoice.method == null) return null;

    return invoice.method;
  }, [invoice.method]);

  //date format
  const formattedDate = useMemo(() => {
    const invoiceDate = new Date(invoice.created_at);
    return format(invoiceDate, "MM/dd/yyyy");
  }, [invoice.created_at]);

  //price format
  const formattedTotal = useMemo(() => {
    return `$${invoice.amount.toFixed(2)}`;
  }, [invoice.amount]);

  //Format card brand
  const formattedCardBrand = useMemo(() => {
    if (!cardInfo?.card_brand) return "N/A";

    const cardBrand = cardInfo.card_brand;
    return cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1);
  }, [cardInfo?.card_brand]);

  const last4 = useMemo(() => {
    if (!cardInfo?.last_4_digits) return "N/A";

    return cardInfo.last_4_digits;
  }, [cardInfo?.last_4_digits]);

  const product = useMemo(() => {
    if (invoice.description == null) return null;

    const description = invoice.description;

    const name = getMedicationForDescription(description.toLowerCase());

    return t(`medications.${name}.capitalized`);
  }, [invoice.description, t]);

  const handleModal = useCallback(() => {
    if (invoice.external_id != null) handleOpenModal(invoice.external_id);
  }, [handleOpenModal, invoice.external_id]);

  return (
    <WhiteCard>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "" : "center"}
        gap={isMobile ? 1 : 0}
        p={1}
      >
        {isMobile ? (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">
                {t(`${page}.date`, "Date")}:
              </Typography>
              <Typography>{formattedDate}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">
                {t(`${page}.product`, "Product")}:
              </Typography>
              <Typography>{product}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">
                {t(`${page}.payment-method`, "Payment method")}:
              </Typography>
              <Typography>{`${formattedCardBrand} ${last4}`}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">
                {t(`${page}.amount`, "Amount")}:
              </Typography>
              <Typography>{formattedTotal}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">
                {t(`${page}.status`, "Status")}:
              </Typography>
              <Typography textTransform="capitalize">
                {t(`invoice.status.${invoice.status}`)}
              </Typography>
            </Stack>
          </>
        ) : (
          <>
            <Typography>{formattedDate}</Typography>
            <Typography>{product}</Typography>
            <Typography>{`${formattedCardBrand} ${last4}`}</Typography>
            <Typography>{formattedTotal}</Typography>
            <Typography textTransform="capitalize">
              {t(`invoice.status.${invoice.status}`)}
            </Typography>
          </>
        )}
        <Stack direction={isMobile ? "column" : "row"} gap={3}>
          {invoice.status === "open" &&
          invoice.product.billing_model !== "one_time" ? (
            <Button variant="secondary" onClick={handleModal}>
              {t(`${page}.pay`)}
            </Button>
          ) : null}
          {invoice.pdf_url != null ? (
            <Link to={invoice.pdf_url} style={{ alignSelf: "center" }}>
              <Typography fontWeight={600} color="#00B6B9">
                {t(`${page}.download`)}
              </Typography>
            </Link>
          ) : null}
        </Stack>
      </Stack>
    </WhiteCard>
  );
};

export default FormattedInvoice;
